import { GroupProject, Project } from 'types/project'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CountOp = 'gt' | 'lt' | 'between'

export type CountFilter = {
  op: CountOp
  /** Operand for gt/lt, lower bound for between */
  min: number | null
  /** Upper bound for between only */
  max: number | null
}

export type DatePreset =
  | 'any'
  | 'today'
  | 'yesterday'
  | 'last_7_days'
  | 'last_30_days'
  | 'last_3_months'
  | 'last_12_months'
  | 'last_3_years'
  | 'on'
  | 'before'
  | 'after'
  | 'range'

/** from/to are unix seconds. Preset-driven ranges are computed at filter-eval time via presetToRange. */
export type DateFilter = {
  preset: DatePreset
  /** Unix seconds. Used by 'on', 'before', 'after', and 'range' (start). */
  from: number | null
  /** Unix seconds. Used by 'range' (end). */
  to: number | null
}

export type ArchivedFilter = 'any' | 'active' | 'archived'

/** Sentinel value mixed into multi-select string[] filters to represent "no value set". */
export const NONE_FILTER_VALUE = '__none__'

export type ProjectFilters = {
  internal: 'any' | 'internal' | 'external'
  archived: ArchivedFilter
  /** Empty = inactive. Match if the project contains ANY of these values. */
  data_types: string[]
  /** Empty = inactive. Match if the project's usage is in this set. */
  usage: string[]
  n_brands: CountFilter | null
  n_products: CountFilter | null
  n_sources: CountFilter | null
  n_datasets: CountFilter | null
  n_posts: CountFilter | null
  n_ratings: CountFilter | null
  /** Empty = inactive. Match if the project's state is in this set. */
  states: string[]
  last_update: DateFilter
  last_record_date: DateFilter
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

export const DEFAULT_DATE_FILTER: DateFilter = {
  preset: 'any',
  from: null,
  to: null,
}

export const DEFAULT_COUNT_FILTER: CountFilter = {
  op: 'gt',
  min: null,
  max: null,
}

export const DEFAULT_PROJECT_FILTERS: ProjectFilters = {
  internal: 'any',
  archived: 'active',
  data_types: [],
  usage: [
    'training',
    'testing',
    'client',
    'trial',
    'pilot',
    'gold_star',
    NONE_FILTER_VALUE,
  ],
  n_brands: null,
  n_products: null,
  n_sources: null,
  n_datasets: null,
  n_posts: null,
  n_ratings: null,
  states: [],
  last_update: DEFAULT_DATE_FILTER,
  last_record_date: DEFAULT_DATE_FILTER,
}

export const EMPTY_PROJECT_FILTERS: ProjectFilters = {
  internal: 'any',
  archived: 'any',
  data_types: [],
  usage: [],
  n_brands: null,
  n_products: null,
  n_sources: null,
  n_datasets: null,
  n_posts: null,
  n_ratings: null,
  states: [],
  last_update: DEFAULT_DATE_FILTER,
  last_record_date: DEFAULT_DATE_FILTER,
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const SECS_PER_DAY = 86400

/**
 * Maps a DatePreset to an inclusive unix-second range.
 * For preset-computed ranges (today/yesterday/last N …), pass the
 * current timestamp so callers can pin it during rendering to avoid
 * per-record clock drift.
 *
 * `any` returns `{ from: null, to: null }` (inactive).
 * `on` / `before` / `after` / `range` return the caller-supplied values
 * passed via the `from`/`to` override params.
 */
export function presetToRange(
  preset: DatePreset,
  now: number = Date.now(),
  from: number | null = null,
  to: number | null = null,
): { from: number | null; to: number | null } {
  const nowSec = Math.floor(now / 1000)

  switch (preset) {
    case 'any':
      return { from: null, to: null }
    case 'today': {
      const startOfToday = nowSec - (nowSec % SECS_PER_DAY)
      return { from: startOfToday, to: nowSec }
    }
    case 'yesterday': {
      const startOfToday = nowSec - (nowSec % SECS_PER_DAY)
      return { from: startOfToday - SECS_PER_DAY, to: startOfToday - 1 }
    }
    case 'last_7_days':
      return { from: nowSec - 7 * SECS_PER_DAY, to: nowSec }
    case 'last_30_days':
      return { from: nowSec - 30 * SECS_PER_DAY, to: nowSec }
    case 'last_3_months':
      return { from: nowSec - 91 * SECS_PER_DAY, to: nowSec }
    case 'last_12_months':
      return { from: nowSec - 365 * SECS_PER_DAY, to: nowSec }
    case 'last_3_years':
      return { from: nowSec - 3 * 365 * SECS_PER_DAY, to: nowSec }
    case 'on':
      // from == to: same day. Caller stores the day's midnight unix sec.
      return { from, to: from }
    case 'before':
      return { from: null, to: from }
    case 'after':
      return { from, to: null }
    case 'range':
      return { from, to }
    default:
      return { from: null, to: null }
  }
}

/**
 * Returns true if `value` satisfies the count filter.
 * A null filter is always satisfied (inactive).
 */
export function matchesCountFilter(
  value: number,
  f: CountFilter | null,
): boolean {
  if (f === null) return true
  const { op, min, max } = f
  if (op === 'gt') {
    if (min === null) return true
    return value > min
  }
  if (op === 'lt') {
    if (min === null) return true
    return value < min
  }
  // between
  if (min === null && max === null) return true
  if (min !== null && value < min) return false
  if (max !== null && value > max) return false
  return true
}

/**
 * Returns true if the unix-second timestamp satisfies the date filter.
 * A missing timestamp is treated as "no date" — only passes when the
 * filter is inactive ('any' or no inputs supplied).
 */
export function matchesDateFilter(
  ts: number | undefined,
  f: DateFilter,
): boolean {
  if (f.preset === 'any') return true
  if (ts === undefined || ts === null) return false

  const { from, to } = presetToRange(f.preset, Date.now(), f.from, f.to)
  if (from !== null && ts < from) return false
  if (to !== null && ts > to) return false
  return true
}

/**
 * Returns the total number of pressed/active selections across all filter
 * dimensions — used as the badge value on the Filters button. Mirrors what
 * the user sees as "selected" in the modal, so default selections also count.
 *
 * - Radio dimensions (internal/archived) contribute 1 unless set to 'any'.
 * - Multi-select dimensions (data_types/usage/states) contribute their length.
 * - Count filters (n_*) contribute 1 each when their min is set.
 * - Date filters contribute 1 each when preset !== 'any'.
 */
export function getActiveFilterCount(filters: ProjectFilters): number {
  let count = 0
  if (filters.internal !== 'any') count++
  if (filters.archived !== 'any') count++
  count += filters.data_types.length
  count += filters.usage.length
  if (filters.n_brands !== null && filters.n_brands.min !== null) count++
  if (filters.n_products !== null && filters.n_products.min !== null) count++
  if (filters.n_sources !== null && filters.n_sources.min !== null) count++
  if (filters.n_datasets !== null && filters.n_datasets.min !== null) count++
  if (filters.n_posts !== null && filters.n_posts.min !== null) count++
  if (filters.n_ratings !== null && filters.n_ratings.min !== null) count++
  count += filters.states.length
  if (filters.last_update.preset !== 'any') count++
  if (filters.last_record_date.preset !== 'any') count++
  return count
}

/**
 * Applies all active project filters to the provided list.
 * Pinning `Date.now()` once here prevents per-row clock drift.
 */
export function applyProjectFilters(
  projects: Project[],
  filters: ProjectFilters,
): Project[] {
  const now = Date.now()

  return projects.filter((p) => {
    // Archived
    if (filters.archived === 'active' && p.archived_at) return false
    if (filters.archived === 'archived' && !p.archived_at) return false

    // Internal
    if (filters.internal !== 'any') {
      if (filters.internal === 'internal' && !p.is_internal) return false
      if (filters.internal === 'external' && p.is_internal) return false
    }

    // Data types
    if (filters.data_types.length > 0) {
      const types = p.data_types ?? []
      const hasNone = filters.data_types.includes(NONE_FILTER_VALUE)
      const realSelected = filters.data_types.filter(
        (v) => v !== NONE_FILTER_VALUE,
      )
      const matches =
        (hasNone && types.length === 0) ||
        realSelected.some((dt) => types.includes(dt))
      if (!matches) return false
    }

    // Usage
    if (filters.usage.length > 0) {
      const hasNone = filters.usage.includes(NONE_FILTER_VALUE)
      const realSelected = filters.usage.filter((v) => v !== NONE_FILTER_VALUE)
      const matches = p.usage ? realSelected.includes(p.usage) : hasNone
      if (!matches) return false
    }

    // Counts
    if (!matchesCountFilter(p.n_brands, filters.n_brands)) return false
    if (!matchesCountFilter(p.n_products, filters.n_products)) return false
    if (!matchesCountFilter(p.n_sources, filters.n_sources)) return false

    const nDatasets =
      (p.groups as GroupProject[] | null | undefined)?.length ?? 0
    if (!matchesCountFilter(nDatasets, filters.n_datasets)) return false

    if (!matchesCountFilter(p.n_posts, filters.n_posts)) return false
    if (!matchesCountFilter(p.n_ratings, filters.n_ratings)) return false

    // Processing status
    if (filters.states.length > 0) {
      if (!filters.states.includes(p.state)) return false
    }

    // Dates — pass unix seconds from the project (already in seconds)
    if (!matchesDateFilterPinned(p.last_update, filters.last_update, now))
      return false
    if (
      !matchesDateFilterPinned(
        p.last_record_date,
        filters.last_record_date,
        now,
      )
    )
      return false

    return true
  })
}

/** matchesDateFilter but with a pinned `now` to avoid per-record drift. */
function matchesDateFilterPinned(
  ts: number | undefined,
  f: DateFilter,
  now: number,
): boolean {
  if (f.preset === 'any') return true
  if (ts === undefined || ts === null) return false

  const { from, to } = presetToRange(f.preset, now, f.from, f.to)
  if (from !== null && ts < from) return false
  if (to !== null && ts > to) return false
  return true
}
